'use client';

import {
  CONTROL_RESPONSE,
  type ExperimentResponse,
  EvaluationContext,
  ExperimentService,
} from '@m/services/experimentation/ExperimentService';
import { useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { useEvaluationContext } from '@m/hooks/data/useEvaluationContext';

type ExperimentQueryOptions = Omit<
  UseQueryOptions<ExperimentResponse, Error>,
  'queryKey' | 'queryFn' | 'enabled'
> & {
  /**
   * Cancel the request and resolve to the control response if evaluation hasn't answered within
   * this many ms. Use when a screen waits on the experiment, so a stalled request can't hold it
   * forever. The cached answer is then control for the session; the arm never flips mid-flow.
   */
  timeoutMs?: number;
};

/** Rejects once `signal` aborts, so a request that ignores its signal still can't outlive it. */
function rejectOnAbort(signal: AbortSignal): Promise<never> {
  return new Promise((_, reject) => {
    signal.addEventListener('abort', () => reject(new Error('Experiment evaluation aborted')), {
      once: true,
    });
  });
}

export const useAssociateStableId = () =>
  useMutation({
    mutationFn: (stableId: string) => ExperimentService.associateStableId(stableId),
  });

export const useExperiment = (
  experimentName: string,
  context?: EvaluationContext,
  enabled = true,
  queryOptions?: ExperimentQueryOptions,
) => {
  const evaluationContext = useEvaluationContext(context);
  const { timeoutMs, ...restQueryOptions } = queryOptions ?? {};

  return useQuery({
    ...restQueryOptions,
    queryKey: ['experiment', experimentName, evaluationContext],
    queryFn: async ({ signal }) => {
      // Aborted by react-query (unmount / cancel) or by the timeout. Either way the request is
      // cancelled instead of finishing in the background, and the timer never outlives the query.
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal.addEventListener('abort', abort, { once: true });
      const timer = timeoutMs === undefined ? undefined : setTimeout(abort, timeoutMs);

      try {
        return await Promise.race([
          ExperimentService.getExperiment(experimentName, evaluationContext, controller.signal),
          rejectOnAbort(controller.signal),
        ]);
      } catch {
        return CONTROL_RESPONSE(experimentName);
      } finally {
        clearTimeout(timer);
        signal.removeEventListener('abort', abort);
      }
    },
    enabled: !!experimentName && enabled,
  });
};
